import { API_BASE_URL, STIP_BASE_URL } from "../../props";
import { useMutation } from "react-query";
import {
  doFileUploadNoAuth,
  doHeadRequest,
  useGet,
  useGetNoAuth,
  usePost,
  usePut,
  useUpdate,
  useUpload,
} from "../../common/fetchServices";
import { addQueryParams, getQueryParams } from "../../util/fetchUtils";
import { useSelector } from "react-redux";

const getContentUrl = id => `${API_BASE_URL}/admin/media/${id}`;
const getPatchContentUrl = ({ id }) => `${API_BASE_URL}/admin/content/${id}`;
const getContentThumbnailUpdateUrl = ({ id }) =>
  `${API_BASE_URL}/admin/content/${id}/thumbnail`;
const getContentFilesUrl = ({ id }) =>
  `${API_BASE_URL}/admin/content/${id}/content_files`;
const getCompoundChildrenUrl = ({ id }) =>
  `${API_BASE_URL}/admin/content/${id}/compound-children`;
const getContentPagesUrl = id => `${API_BASE_URL}/admin/content/${id}/pages`;
const getFileUploadRequestUrl = () => `${STIP_BASE_URL}/file-upload/request`;
const getCreatorUploadUrl = ({ mediaId }) =>
  `${STIP_BASE_URL}/creator/upload/${mediaId}`;

//TODO: Assume a single image for now, so append _0001 to storageId
//      We will eventually grab this information from contentFile table
export const getIiifUrl = (iiifParameters, { media, token }) =>
  `${API_BASE_URL}/iiif/3/${media.id}/${media.storageId}_0001${iiifParameters}?bibauth=${token}`;

export const useMedia = id => {
  const url = getContentUrl(id);
  return useGet(["content", { id }], url);
};

export const useContentUpdate = () => {
  return useUpdate(getPatchContentUrl, {
    queriesToInvalidate: [
      "chapters",
      ["content", ["id"]],
      ["content-badges", ["id"]],
      ["content-status"],
      "search",
    ],
  });
};

export const useUploadContentThumbnail = () => {
  return useUpload(getContentThumbnailUpdateUrl);
};

export const useContentPages = (id, params) => {
  const queryParams = getQueryParams(params);
  const url = getContentPagesUrl(id) + `?${queryParams}`;
  return useGet(["content-pages", { id, url }], url);
};

export const useContentFiles = ({ id, limit = "all" }) => {
  const url = getContentFilesUrl({ id }) + `?limit=${limit}`;
  return useGet(["content-files", { id }], url);
};

export const useUpdateContentFiles = () => {
  return usePut(getContentFilesUrl, {
    queriesToInvalidate: [
      ["chapters", ["id"]],
      ["content-files", ["id"]],
    ],
  });
};

export const useCompoundChildren = id => {
  const url = getCompoundChildrenUrl({ id });
  return useGet(["compound-children", { id }], url);
};

export const useChapters = (id, type, { limit = "all" } = {}) => {
  const baseUrl =
    type === "AUDIOBOOK"
      ? getContentFilesUrl({ id })
      : getCompoundChildrenUrl({ id });

  const url = addQueryParams(baseUrl, { limit });
  return useGet(["chapters", { id }], url);
};

export const useFileUploadRequest = () => {
  return usePost(getFileUploadRequestUrl);
};

export const useUploadS3Direct = () => {
  return useMutation(({ url, fileBlob }) => {
    return doFileUploadNoAuth(url, fileBlob);
  });
};

export const useUpdateContentMediaFile = () => {
  return usePost(getCreatorUploadUrl);
};

export const useIiifUrl = (media, iiifParameters = "/info.json") => {
  const auth = useSelector(state => state.auth);
  const url = getIiifUrl(media, auth.user.token);
  return useGetNoAuth(["iiif", { id: media.id }], url + iiifParameters);
};

export const getOptimizedPdfUrl = async content => {
  const { pdfUrl, mimeType } = content;

  if (pdfUrl && mimeType !== "image/tiff") {
    const optimizedPdfUrl = pdfUrl
      ? pdfUrl.replace("content.pdf", "content_optimized.pdf")
      : "";
    try {
      const res = await doHeadRequest(optimizedPdfUrl);
      const fileExists = res.status === 200 && res.statusText === "OK";
      if (fileExists) {
        return optimizedPdfUrl;
      }

      if (content.type === "CHAPTER") {
        return pdfUrl;
      }

      return "";
    } catch (err) {
      return "";
    }
  }

  return "";
};
